// @flow
import React, { type Node } from 'react';
import classnames from 'classnames';
import invariant from 'invariant';

import stylesheet from './Button.scss';

const asTypes = {
  'solid-teal': 'solid-teal',
  'outline-teal': 'outline-teal',
  'solid-neutral-dark-100': 'solid-neutral-dark-100',
  'outline-neutral-dark-300': 'outline-neutral-dark-300',
  'solid-neutral-light-1000': 'solid-neutral-light-1000',
  'outline-neutral-light-500': 'outline-neutral-light-500',
  'solid-volcano': 'solid-volcano',
  'solid-legacy': 'solid-legacy',
  'no-styling': 'no-styling',
};

const sizeTypes = {
  small: 'small',
  medium: 'medium',
  large: 'large',
};

type AsType = $Keys<typeof asTypes>;
type SizeType = $Keys<typeof sizeTypes>;

type Props = {|
  children: Node,
  /** Available size: 'small' | 'medium' | 'large' */
  size?: SizeType,
  /** Available as: 'solid-teal' | 'outline-teal' | 'solid-neutral-dark-100' | 'outline-neutral-dark-300' | 'solid-neutral-light-1000' | 'outline-neutral-light-500' | 'solid-volcano' | 'solid-legacy' | 'no-styling' */
  as: AsType,
  type?: 'button' | 'reset' | 'submit',
  onClick?: (e: SyntheticEvent<HTMLButtonElement>) => void,
|};

const Button = ({
  children,
  size = '',
  as,
  type = 'button',
  onClick,
}: Props) => {
  invariant(
    asTypes[as],
    'You must specify a valid value for the "as" prop when rendering a Button'
  );

  if (type !== 'submit') {
    invariant(
      !!onClick,
      "Button requires an onClick handler. If your button doesn't have interactivity consider using <Anchor />"
    );
  }

  const buttonClass = classnames(
    stylesheet.button,
    stylesheet[`button--${as}`],
    { [stylesheet[`button--${size}`]]: size }
  );
  return (
    <button className={buttonClass} onClick={onClick} type={type}>
      {children}
    </button>
  );
};

export default Button;
